import refreshLocationSuggestions from '../utils/refreshLocationSuggestions';
import isDirty from '../utils/isDirty';
import { getFolderIdByCalendarID } from 'owa-calendar-cache';
import { notifyOnCalendarIdChanged } from 'owa-calendar-compose-form-store';
import shouldSupportAutoRoomBooking from 'owa-calendar-compose-form-store/lib/selectors/shouldSupportAutoRoomBooking';
import shouldShowTimeSuggestions from 'owa-calendar-compose-form-store/lib/selectors/shouldShowTimeSuggestions';
import {
    getComposeEvent,
    getOriginalEvent,
    getFullComposeViewState,
} from 'owa-calendar-compose-store-manager';
import { updateFolderIds } from 'owa-calendar-scheduling-grid';
import type { ClientFolderId } from 'owa-client-ids';
import { orchestrator } from 'satcheljs';
import {
    onCalendarIdChanged,
    onAccountChanged,
    onParentFolderIdChanged,
    setRoomBookingAssistantEnabled,
    onSkypeTeamsPropertiesChanged,
    initializeTimeSuggestionMetricsInfo,
    prepareEvent,
} from 'owa-calendar-compose-form-actions';

import {
    composeFormChangeAccountResetChangesTitle,
    composeFormChangeAccountResetChangesSubText,
    composeFormChangeAccountResetChangesOKText,
    composeFormChangeAccountResetChangesCancelText,
} from './onCalendarIdChangedOrchestrator.locstring.json';
import { confirm, DialogResponse } from 'owa-confirm-dialog';
import { lazyInitializeCalendarChannelPicker } from 'owa-calendar-channel-picker';
import loc from 'owa-localize';
import type { CalendarId } from 'owa-graph-schema';

export default orchestrator(
    onCalendarIdChanged,
    async (actionMessage: { CalendarId: CalendarId; formId: string }) => {
        const { CalendarId, formId } = actionMessage;
        const event = getComposeEvent(formId);
        const originalEvent = getOriginalEvent(formId);
        // Call prepareEvent() since we want to extract the properties from the viewState before generating event diff
        prepareEvent(formId);

        const isFormDirty = isDirty(
            event,
            originalEvent,
            false /* shouldIgnoreAttachments */,
            true /* shouldIgnoreParentFolderId */
        );
        const isAccountChange =
            CalendarId.mailboxInfo.userIdentity != event.ParentFolderId.mailboxInfo.userIdentity;

        if (isAccountChange && isFormDirty) {
            const response = await confirm(
                loc(composeFormChangeAccountResetChangesTitle),
                loc(composeFormChangeAccountResetChangesSubText),
                false,
                {
                    okText: loc(composeFormChangeAccountResetChangesCancelText),
                    continueText: loc(composeFormChangeAccountResetChangesOKText),
                    hideCancelButton: true,
                    showContinueButton: true,
                }
            );

            if (response === DialogResponse.continue) {
                notifyOnCalendarIdChanged(CalendarId);

                const folderId: ClientFolderId = {
                    Id: getFolderIdByCalendarID(CalendarId.id),
                    mailboxInfo: CalendarId.mailboxInfo,
                };

                onAccountChanged(folderId, formId);
            } else {
                lazyInitializeCalendarChannelPicker.importAndExecute(event, formId);
            }
        } else {
            // The legacy actions call no-op satchelv2 actions so the stitches (that still use v2 subscriptions) will fire correctly
            notifyOnCalendarIdChanged(CalendarId);

            const fullComposeViewState = getFullComposeViewState(formId);
            if (event) {
                let folderId: ClientFolderId = {
                    Id: getFolderIdByCalendarID(CalendarId.id),
                    mailboxInfo: CalendarId.mailboxInfo,
                };

                onParentFolderIdChanged(folderId, formId);
                refreshLocationSuggestions(formId);

                if (isAccountChange && shouldShowTimeSuggestions(formId)) {
                    initializeTimeSuggestionMetricsInfo(formId);
                }

                // Full compose can be null when the opened form is Quick compose
                if (fullComposeViewState) {
                    updateFolderIds(fullComposeViewState.schedulingGridScenarioId, [folderId.Id]);

                    if (!shouldSupportAutoRoomBooking(formId)) {
                        setRoomBookingAssistantEnabled(false, formId);
                    }
                }

                // Reset SkypeTeamsProperties when the selected option in the calendar picker is a calendar and not a channel
                onSkypeTeamsPropertiesChanged(null, formId);
            }
        }
    }
);
